import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { DataTable } from "@/components/data";
import type { DataTablePage } from "@/components/data";
import type { ColumnDef } from "@tanstack/react-table";
import { FreshnessLine } from "@/components/shell/FreshnessLine";
import { AlertCard } from "@/modules/protex/components/AlertCard";

import { assetKey, getAsset, messageFor, type AssetEval } from "./api";
import { FindingCard } from "./components/FindingCard";

/**
 * Asset detail (UX §6 "Asset"). Opened from the Accuracy findings table or
 * from a stage's "See accuracy findings" link. URL state is `?month=&asset=`
 * so the back link can restore the table's month and filters unchanged.
 */
export function AssetDetail({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const assetId = typeof search.asset === "string" ? search.asset : null;

  const query = useQuery({
    queryKey: assetKey(assetId ?? ""),
    queryFn: () => getAsset(assetId as string),
    enabled: Boolean(assetId),
  });

  const back = () => {
    const next = { ...search };
    delete next.asset;
    void navigate({ to: ".", search: next, replace: false });
  };

  if (!assetId) return null;

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <p role="status" className="sr-only">
          Loading the asset…
        </p>
        {[0, 1].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-ks-md bg-ks-grey-4/40" />
        ))}
      </div>
    );
  }

  if (query.isError || query.data?.data.available === false) {
    return (
      <div className="flex flex-col gap-3">
        <BackLink onBack={back} />
        <AlertCard
          title="We couldn't load this asset"
          detail={query.error ? messageFor(query.error, areaName) : "Try again shortly."}
        />
      </div>
    );
  }

  const data = query.data!.data;
  const openFindings = data.findings.filter((f) => f.status === "open" && f.newer_draft_since_review);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 rounded-ks-md border border-ks-grey-4 bg-ks-surface px-3 py-2">
        <div className="flex items-center gap-2">
          <BackLink onBack={back} />
          <span className="text-sm font-semibold text-ks-ink">{assetId}</span>
        </div>
      </div>
      <FreshnessLine meta={query.data!.meta} />

      {data.review === null ? (
        <p className="text-sm text-ks-ink">This asset has not been reviewed.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 text-sm text-ks-ink">
            <span>
              Reviewed revision <b>{data.review.reviewed_revision}</b>
            </span>
            <span>
              Reviewer <b>{data.review.reviewer}</b>
            </span>
            <span>
              <b>{data.review.finding_count}</b> finding{data.review.finding_count === 1 ? "" : "s"}
            </span>
          </div>

          {openFindings.length > 0 && (
            <h2 className="text-base font-semibold text-ks-ink">
              {openFindings.length} finding{openFindings.length === 1 ? "" : "s"} waiting for re-check
            </h2>
          )}

          <div className="flex flex-col gap-2">
            {data.findings.map((finding) => (
              <FindingCard key={finding.finding_id} finding={finding} />
            ))}
          </div>

          <EvalTable evals={data.evals} />
        </>
      )}
    </div>
  );
}

function EvalTable({ evals }: { evals: AssetEval[] }) {
  if (evals.length === 0) return null;

  const page: DataTablePage = { pageIndex: 0, pageSize: evals.length, rowCount: evals.length };
  const columns: ColumnDef<AssetEval, unknown>[] = [
    { accessorKey: "draft_n", header: "#" },
    {
      accessorKey: "created_at",
      header: "Date",
      cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
    },
    { accessorKey: "eval_verdict", header: "Verdict" },
    { accessorKey: "score_overall", header: "Overall" },
    { accessorKey: "score_snippet_integrity", header: "Snippet integrity" },
    { accessorKey: "score_communication", header: "Communication" },
    { accessorKey: "score_kb_consistency", header: "KB consistency" },
  ];

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-base font-semibold text-ks-ink">Eval history</h2>
      <DataTable columns={columns} rows={evals} page={page} onPageChange={() => {}} />
    </div>
  );
}

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      data-nonmutating
      onClick={onBack}
      className="w-fit text-left text-sm text-ks-ink underline-offset-2 hover:underline"
    >
      ← Back to findings
    </button>
  );
}
